import {
	FunctionComponent,
	KeyboardEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { faThumbtack } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

/** A single restored criterion shown in the popover summary ("Source: LinkedIn"). */
export interface PinSummaryItem {
	label: string;
	value: string;
}

interface PinFilterButtonProps {
	/** Disabled when the current view has nothing worth pinning. */
	disabled: boolean;
	/** Pre-filled tab name suggestion. */
	suggestedName: string;
	/** Human-readable description of the filters the tab will restore. */
	summaryItems: PinSummaryItem[];
	/** Persist a new pinned tab with the given (already-trimmed-or-fallback) name. */
	onPin: (name: string) => void;
	/** Shadow-root portal target for the extension; defaults to document.body on the web. */
	portalContainer?: HTMLElement | null;
}

/**
 * "Pin to tab" button with an anchored popover for naming the saved view. Rendered in the soft
 * (tinted) action style so it reads as an opt-in affordance beside the filters rather than
 * competing with the row's primary button — shared verbatim by the Lead List overview and the
 * Campaigns overview (PREC-3880), which is why the two must never diverge visually.
 *
 * The popover is portalled (extension-safe via {@link PinFilterButtonProps.portalContainer}) and
 * positioned with fixed viewport coordinates so it is never clipped by the filter bar. Its own
 * Cancel/Pin buttons keep the standard secondary/primary pair — the soft style applies to the
 * trigger only.
 */
export const PinFilterButton: FunctionComponent<PinFilterButtonProps> = ({
	disabled,
	suggestedName,
	summaryItems,
	onPin,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.pinTab.${key}`);

	const wrapperRef = useRef<HTMLDivElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [name, setName] = useState(suggestedName);
	const [position, setPosition] = useState<{ top: number; right: number }>({ top: 0, right: 0 });

	const updatePosition = useCallback(() => {
		const rect = wrapperRef.current?.getBoundingClientRect();

		if (rect) {
			setPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
		}
	}, []);

	const close = useCallback(() => setIsOpen(false), []);

	const open = () => {
		setName(suggestedName);
		setIsOpen(true);
	};

	useLayoutEffect(() => {
		if (isOpen) {
			updatePosition();
		}
	}, [isOpen, updatePosition]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleOutside = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (!wrapperRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
				close();
			}
		};

		window.addEventListener("mousedown", handleOutside);
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("mousedown", handleOutside);
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [isOpen, close, updatePosition]);

	const save = () => {
		onPin(name.trim() || suggestedName);
		close();
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			save();
		} else if (event.key === "Escape") {
			close();
		}
	};

	const popover = (
		<div
			ref={popoverRef}
			className="fixed z-[200] w-[300px] rounded-xl border border-border-light bg-white p-4 shadow-xl"
			style={{ top: position.top, right: position.right }}
		>
			<h4 className="text-sm font-bold text-text">{ts("popoverTitle")}</h4>
			<p className="mb-3 mt-0.5 text-xs text-text-meta">{ts("popoverSubtitle")}</p>

			<label className="mb-1.5 block text-xxs font-bold uppercase tracking-wide text-text-meta">
				{ts("nameLabel")}
			</label>
			<input
				autoFocus
				className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-text outline-none focus:border-brand-light"
				placeholder={ts("namePlaceholder")}
				value={name}
				onChange={(event) => setName(event.target.value)}
				onKeyDown={handleKeyDown}
			/>

			{summaryItems.length > 0 && (
				<div className="my-3 rounded-lg border border-border-light bg-gray-50 px-3 py-2 text-xs text-text-meta">
					{summaryItems.map((item) => (
						<div key={item.label}>
							<span className="font-bold text-text">{item.label}:</span> {item.value}
						</div>
					))}
				</div>
			)}

			<div className="mt-3 flex justify-end gap-2">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.S}
					title={ts("cancel")}
					type="button"
					onClick={close}
				/>
				<Button
					color={ButtonColor.ACTION}
					image={<Icon className="mr-1.5 h-3.5 w-3.5" icon={faThumbtack} />}
					size={ButtonSize.S}
					title={ts("save")}
					type="button"
					onClick={save}
				/>
			</div>
		</div>
	);

	return (
		<div ref={wrapperRef} className="relative flex-shrink-0">
			<Button
				color={ButtonColor.ACTION_SOFT}
				disabled={disabled}
				image={<Icon className="mr-1.5 h-3.5 w-3.5" icon={faThumbtack} />}
				size={ButtonSize.S}
				testId="pin-to-tab-button"
				title={ts("trigger")}
				type="button"
				onClick={() => (isOpen ? close() : open())}
			/>
			{isOpen && createPortal(popover, portalContainer ?? document.body)}
		</div>
	);
};
